<template>
  <div class="inquiry-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ formTitle }}</h4>
        <b-badge variant="light-primary" class="ml-2">ID: {{ inquiryId }}</b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="!inquiry" class="empty-state">
      <feather-icon icon="AlertCircleIcon" size="42" class="empty-state__icon" />
      <p class="empty-state__text">{{ $t("No inquiry data found.") }}</p>
    </div>

    <div v-else class="print-stack">
      <div class="inquiry-sheet">
        <div class="sheet-content">
          <header class="doc-header">
            <div class="doc-header__brand">
              <div class="doc-header__logo">
                <img
                  v-if="schoolLogo"
                  :src="schoolLogo"
                  alt="School logo"
                  class="doc-header__logo-img"
                />
                <div v-else class="doc-header__logo-fallback">
                  <feather-icon icon="AwardIcon" size="28" />
                </div>
              </div>
              <div class="doc-header__school">
                <h1 class="doc-header__school-name">{{ campusName }}</h1>
                <p v-if="campusAddress" class="doc-header__school-address">
                  {{ campusAddress }}
                </p>
              </div>
            </div>

            <div class="doc-header__title-block">
              <h2 class="doc-header__title">{{ formTitle }}</h2>
              <p class="doc-header__ref">{{ $t("Reference") }}: INQ-{{ inquiryId }}</p>
            </div>
          </header>

          <div class="summary-cards">
            <div class="summary-card">
              <span class="summary-card__icon-wrap">
                <feather-icon icon="ActivityIcon" size="16" />
              </span>
              <div class="summary-card__content">
                <span class="summary-card__label">{{ $t("Status") }}</span>
                <span class="status-pill" :class="statusBadgeClass">
                  {{ displayStatus }}
                </span>
              </div>
            </div>
            <div class="summary-card">
              <span class="summary-card__icon-wrap">
                <feather-icon icon="CalendarIcon" size="16" />
              </span>
              <div class="summary-card__content">
                <span class="summary-card__label">{{ $t("Print Date") }}</span>
                <span class="summary-card__value">{{ printDate }}</span>
              </div>
            </div>
          </div>

          <div class="sheet-body">
            <section
              v-for="section in sections"
              :key="section.title"
              class="report-section"
            >
              <div class="report-section__head">
                <span class="report-section__marker"></span>
                <h3 class="report-section__title">{{ section.title }}</h3>
              </div>

              <div class="data-grid">
                <div
                  v-for="(field, index) in section.fields"
                  :key="`${section.title}-${index}`"
                  class="data-cell"
                  :class="{
                    'data-cell--wide': field.fullWidth,
                    'data-cell--multiline': field.multiline,
                    'data-cell--empty': field.value === '—',
                  }"
                >
                  <span class="data-cell__label">{{ field.label }}:</span>
                  <span class="data-cell__value">{{ field.value }}</span>
                </div>
              </div>
            </section>

            <footer class="signature-footer">
              <div class="signature-footer__block">
                <div class="signature-footer__line"></div>
                <span class="signature-footer__label">{{ $t("Prepared By") }}</span>
              </div>
              <div class="signature-footer__block">
                <div class="signature-footer__line"></div>
                <span class="signature-footer__label">{{ $t("Verified By") }}</span>
              </div>
              <div class="signature-footer__block">
                <div class="signature-footer__line"></div>
                <span class="signature-footer__label">{{ $t("Received By") }}</span>
              </div>
            </footer>

            <div class="doc-footer">
              <span>{{ campusName }}</span>
              <span>{{ $t("Generated on") }} {{ printDate }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const STORAGE_KEY = "inquiryPrintPayload";

export default {
  name: "InquiryDetailPrint",
  data() {
    return {
      inquiry: null,
      className: "",
      referenceName: "",
      campusName: "MY SKOOL",
      campusAddress: "",
      schoolLogo: null,
      reportDomain: "",
      originalPageTitle: "",
    };
  },
  computed: {
    inquiryId() {
      return this.inquiry?.id || "—";
    },
    isVisitor() {
      return this.inquiry?.inquiryType === "visitor";
    },
    formTitle() {
      return this.isVisitor
        ? this.$t("Visitor Inquiry Form")
        : this.$t("Student Inquiry Form");
    },
    displayStatus() {
      const status = this.inquiry?.inquiryStatus;
      return status ? status : this.$t("pending");
    },
    statusBadgeClass() {
      const status = String(this.displayStatus || "").toLowerCase();
      if (["approved", "confirmed", "present", "enrolled"].includes(status)) {
        return "status-pill--success";
      }
      if (["rejected", "closed", "cancelled", "left"].includes(status)) {
        return "status-pill--danger";
      }
      if (["follow up", "followup", "pending", "registered"].includes(status)) {
        return "status-pill--warning";
      }
      return "status-pill--neutral";
    },
    printDate() {
      return this.formatDate(new Date());
    },
    sections() {
      if (!this.inquiry) return [];

      const inquiry = this.inquiry;
      const personal = [
        { label: this.$t("Name"), value: inquiry.name },
        { label: this.$t("Father name"), value: inquiry.father_name },
        { label: this.$t("Contact number"), value: inquiry.contact },
        { label: this.$t("Email"), value: inquiry.email },
        { label: this.$t("CNIC"), value: inquiry.cnic, show: this.isVisitor },
      ].filter((field) => field.show !== false);

      const isHasanIslamic = this.reportDomain === "myskoolhasanislamic";

      const academic = [
        { label: this.$t("Current class"), value: inquiry.currentClass },
        {
          label: this.isVisitor
            ? this.$t("Grade applying for")
            : this.$t("Class"),
          value: this.className,
        },
        ...(isHasanIslamic && inquiry.sec
          ? [{ label: this.$t("Section"), value: inquiry.sec }]
          : []),
        {
          label: isHasanIslamic
            ? this.$t("Last Institution")
            : this.$t("Last School"),
          value: inquiry.lastSchool,
        },
        {
          label: this.$t("Session"),
          value: inquiry.sessionID > 0 ? inquiry.sessionID : "",
        },
        { label: this.$t("Last Monthly Fee"), value: inquiry.lastMonthlyFee },
        { label: this.$t("Studying Since"), value: inquiry.studyingSince },
      ];

      const crestview = [
        { label: this.$t("Date of birth"), value: this.formatDate(inquiry.dob) },
        { label: this.$t("Age"), value: inquiry.age },
        {
          label: this.$t("Learning difficulties"),
          value: inquiry.learningDifficulties,
        },
        {
          label: this.$t("Special medical needs"),
          value: inquiry.specialMedicalNeeds,
        },
        {
          label: this.$t("Father's occupation"),
          value: inquiry.father_occupation,
        },
        {
          label: this.$t("Mother's occupation"),
          value: inquiry.mother_occupation,
        },
        { label: this.$t("Father's CNIC Number"), value: inquiry.father_cnic },
        { label: this.$t("Mother's CNIC Number"), value: inquiry.mother_cnic },
      ];

      const inquiryDetails = [
        { label: this.$t("Date"), value: this.formatDate(inquiry.date) },
        {
          label: this.$t("Next follow up date"),
          value: this.formatDate(inquiry.nextFollowup),
        },
        { label: this.$t("Purpose"), value: inquiry.purpose },
        { label: this.$t("Tag"), value: inquiry.tag },
        { label: this.$t("Reference"), value: this.referenceName },
        { label: this.$t("Residential Area"), value: inquiry.residentialArea },
        {
          label: this.$t("Note"),
          value: inquiry.note,
          fullWidth: true,
          multiline: true,
        },
        {
          label: this.$t("Final Remarks"),
          value: inquiry.finalRemarks,
          fullWidth: true,
          multiline: true,
        },
      ];

      const visitorDetails = [
        {
          label: this.$t("Visiting Time"),
          value: this.formatTime(inquiry.visitingTime),
        },
        {
          label: this.$t("Checkout Time"),
          value: this.formatTime(inquiry.checkoutTime),
        },
        { label: this.$t("Meeting with"), value: inquiry.meetingWith },
      ];

      const sections = [
        { title: this.$t("Personal Information"), fields: personal },
        { title: this.$t("Academic Information"), fields: academic },
      ];

      if (this.reportDomain === "myskoolcrestviewacademy") {
        sections.push({
          title: this.$t("Additional Information"),
          fields: crestview,
        });
      }

      sections.push({
        title: this.$t("Inquiry Details"),
        fields: inquiryDetails,
      });

      if (this.isVisitor) {
        sections.push({
          title: this.$t("Visit Details"),
          fields: visitorDetails,
        });
      }

      return sections.map((section) => ({
        title: section.title,
        fields: section.fields.map((field) => ({
          label: field.label,
          value: this.displayValue(field.value),
          fullWidth: !!field.fullWidth,
          multiline: !!field.multiline,
        })),
      }));
    },
  },
  created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    this.loadPayload();
    this.loadCampusInfo();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  methods: {
    loadPayload() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const payload = JSON.parse(raw);
        this.inquiry = payload.inquiry || null;
        this.className = payload.className || "";
        this.referenceName =
          payload.referenceName || payload.inquiry?.referenceOther || "";
        this.campusName = payload.campusName || "MY SKOOL";
        this.reportDomain = payload.reportDomain || "";
      } catch (error) {
        this.inquiry = null;
      }
    },
    displayValue(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return value;
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },
    formatTime(value) {
      if (!value) return "—";
      if (String(value).includes("T")) {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          return date.toLocaleTimeString("en-UK", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }
      return String(value);
    },
    async loadCampusInfo() {
      const cID =
        this.inquiry?.campusID ||
        this.$store.state.userData?.cId ||
        this.$route.query.cID;
      const db = this.$store.state.userData?.db || this.$route.query.db;

      if (!db || !cID) {
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            this.campusName;
          this.campusAddress =
            currentBranch.address || currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || null;
        }
        return;
      }

      try {
        const url = `${this.$store.state.domain}campuses`;
        const response = await axios.get(url, {
          params: { db, cId: cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == cID);
          if (match) {
            this.campusName = match.name || match.branch || this.campusName;
            this.campusAddress = match.address || match.branch || "";
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (error) {
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            this.campusName;
          this.campusAddress =
            currentBranch.address || currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || null;
        }
      }
    },
    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
$brand: #4f46e5;
$brand-dark: #312e81;
$ink: #0f172a;
$muted: #64748b;
$line: #e2e8f0;
$surface: #f8fafc;

.inquiry-print {
  background: linear-gradient(180deg, #e8edf5 0%, #f4f6fa 100%);
  min-height: 100vh;
  padding: 1.5rem;
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
  color: $ink;
  -webkit-font-smoothing: antialiased;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: #fff;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.empty-state {
  max-width: 420px;
  margin: 4rem auto;
  text-align: center;
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.empty-state__icon {
  color: #ef4444;
  margin-bottom: 0.75rem;
}

.empty-state__text {
  margin: 0;
  font-weight: 600;
  color: #475569;
}

.print-stack {
  max-width: 210mm;
  width: 100%;
  margin: 0 auto;
}

.inquiry-sheet {
  position: relative;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 20px 50px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.9);
  page-break-inside: avoid;
}


.sheet-content {
  padding: 1.35rem 1.5rem 1.15rem;
}

.doc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $line;
}

.doc-header__brand {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
  flex: 1;
}

.doc-header__logo {
  width: 72px;
  height: 72px;
  border-radius: 14px;
  border: 1px solid $line;
  background: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.doc-header__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.35rem;
}

.doc-header__logo-fallback {
  color: $brand;
}

.doc-header__school-name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.25;
  color: $ink;
  letter-spacing: -0.01em;
}

.doc-header__school-address {
  margin: 0.28rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: $muted;
  max-width: 360px;
}

.doc-header__title-block {
  text-align: right;
  flex-shrink: 0;
}

.doc-header__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: $ink;
  letter-spacing: -0.02em;
}

.doc-header__ref {
  margin: 0.35rem 0 0;
  font-size: 0.76rem;
  color: $muted;
  font-weight: 600;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 0.25rem;
}

.summary-card {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.8rem 0.85rem;
  background: $surface;
  border: 1px solid $line;
  border-radius: 12px;
}

.summary-card__icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #eef2ff;
  color: $brand;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-card__content {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  min-width: 0;
}

.summary-card__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $muted;
}

.summary-card__value {
  font-size: 0.92rem;
  font-weight: 700;
  color: $ink;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 0.22rem 0.62rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-pill--success {
  background: #dcfce7;
  color: #166534;
}

.status-pill--warning {
  background: #fef3c7;
  color: #92400e;
}

.status-pill--danger {
  background: #fee2e2;
  color: #991b1b;
}

.status-pill--neutral {
  background: #e2e8f0;
  color: #334155;
}

.sheet-body {
  margin-top: 0.35rem;
}

.report-section {
  margin-top: 1rem;
  border: 1px solid $line;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.report-section__head {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.65rem 0.9rem;
  background: linear-gradient(90deg, #f8fafc 0%, #fff 100%);
  border-bottom: 1px solid $line;
}

.report-section__marker {
  width: 4px;
  height: 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, $brand 0%, #818cf8 100%);
  flex-shrink: 0;
}

.report-section__title {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $ink;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.data-cell {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0.35rem;
  padding: 0.5rem 0.9rem;
  border-right: 1px solid $line;
  border-bottom: 1px solid $line;
  min-height: auto;
  background: #fff;
}

.data-cell:nth-child(4n + 3),
.data-cell:nth-child(4n + 4) {
  background: #fcfdff;
}

.data-cell:nth-child(2n) {
  border-right: none;
}

.data-cell--wide {
  grid-column: 1 / -1;
  border-right: none;
}

.data-cell--multiline {
  align-items: flex-start;
}

.data-cell--multiline .data-cell__value {
  white-space: pre-wrap;
}

.data-cell--empty .data-cell__value {
  color: #94a3b8;
}

.data-cell__label {
  font-size: 0.72rem;
  font-weight: 700;
  color: $muted;
  white-space: nowrap;
  flex-shrink: 0;
}

.data-cell__value {
  flex: 1;
  min-width: 0;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.35;
  color: $ink;
  word-break: break-word;
}

.signature-footer {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 1.35rem;
  padding: 1rem 0.25rem 0;
}

.signature-footer__block {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.signature-footer__line {
  border-top: 1px solid #94a3b8;
  margin: 2.5rem 0.5rem 0.5rem;
}

.signature-footer__label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
}

.doc-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid $line;
  font-size: 0.68rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

@media screen and (max-width: 768px) {
  .inquiry-print {
    padding: 0.75rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .action-buttons {
    justify-content: flex-end;
  }

  .doc-header {
    flex-direction: column;
  }

  .doc-header__title-block {
    text-align: left;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }

  .data-grid {
    grid-template-columns: 1fr;
  }

  .data-cell,
  .data-cell:nth-child(2n) {
    border-right: none;
  }

  .signature-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .doc-footer {
    flex-direction: column;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .inquiry-print {
    background: #fff !important;
    padding: 0 !important;
  }

  .print-stack {
    max-width: 100% !important;
    margin: 0 !important;
  }

  .inquiry-sheet {
    border-radius: 0 !important;
    box-shadow: none !important;
    border: none !important;
  }

  .sheet-content {
    padding: 1.1rem 1.2rem !important;
  }

  .summary-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .data-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .signature-footer {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: flex-end !important;
    gap: 0.75rem !important;
    page-break-inside: avoid;
  }

  .signature-footer__block {
    flex: 1 1 33.333% !important;
    min-width: 0 !important;
  }

  .signature-footer__line {
    margin: 2rem 0.35rem 0.4rem !important;
  }

  .doc-footer {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
  }

  .summary-card,
  .report-section,
  .data-cell:nth-child(4n + 3),
  .data-cell:nth-child(4n + 4),
  .data-cell {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .data-cell {
    flex-direction: row !important;
    align-items: baseline !important;
    padding: 0.38rem 0.75rem !important;
    page-break-inside: avoid;
  }

  .data-cell__label {
    white-space: nowrap !important;
  }

  .data-cell__value {
    white-space: normal !important;
  }

  .status-pill,
  .report-section__head,
  .summary-card__icon-wrap {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
